import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import type { DesignThemeConfig } from '@/stores/design-preview-store';

export interface SandboxDraft {
  id: string;
  name: string;
  source: 'manual' | 'gallery' | 'ai' | 'backup';
  created_by: string | null;
  created_at: string;
  updated_at: string;
  /** Only present when a single draft is fetched by id. */
  config?: DesignThemeConfig;
}

/** Cache key for the saved-drafts list — distinct from useSettings' ['settings']. */
export const SANDBOX_DRAFTS_KEY = ['design-sandbox-drafts'] as const;

const BASE = '/admin/design-sandbox';

export function useSandboxDrafts() {
  return useQuery({
    queryKey: SANDBOX_DRAFTS_KEY,
    queryFn: () => api.get<{ drafts: SandboxDraft[] }>(`${BASE}/drafts`).then((r) => r.drafts),
  });
}

export function useCreateDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; config: DesignThemeConfig; source?: 'manual' | 'gallery' | 'ai' }) =>
      api.post<{ draft: SandboxDraft }>(`${BASE}/drafts`, body).then((r) => r.draft),
    onSuccess: () => qc.invalidateQueries({ queryKey: SANDBOX_DRAFTS_KEY }),
  });
}

export function useUpdateDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name?: string; config?: DesignThemeConfig }) =>
      api.put<{ draft: SandboxDraft }>(`${BASE}/drafts/${id}`, body).then((r) => r.draft),
    onSuccess: () => qc.invalidateQueries({ queryKey: SANDBOX_DRAFTS_KEY }),
  });
}

export function useDeleteDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`${BASE}/drafts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: SANDBOX_DRAFTS_KEY }),
  });
}

/** Fetch a single draft's full config on demand (the list omits the blob). */
export function useFetchDraftConfig() {
  return useMutation({
    mutationFn: (id: string) =>
      api.get<{ draft: SandboxDraft }>(`${BASE}/drafts/${id}`).then((r) => r.draft),
  });
}

/**
 * Expand a candidate's base colors into the full `generated` palette (the same
 * expansion the live site applies on read), so the sandbox preview shows the
 * whole-palette swap a publish will produce. Server-side and dependency-free
 * (chroma-js lives in functions, not web), so the preview is byte-identical to
 * what visitors get. Derived only — persists nothing.
 */
export function useExpandDesign() {
  return useMutation({
    mutationFn: (config: DesignThemeConfig) =>
      api
        .post<{ config: DesignThemeConfig }>(`${BASE}/expand`, { config, fullScope: true })
        .then((r) => r.config),
  });
}

/**
 * Publish a candidate design to the live site. The server snapshots the current
 * live theme as a `backup` draft first, so it can be reverted. We invalidate
 * ['settings'] so the admin's own non-preview views pick up the change.
 */
export function usePublishDesign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (config: DesignThemeConfig) =>
      api.post<{ success: boolean; backup_id: string | null }>(`${BASE}/publish`, { config }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] });
      qc.invalidateQueries({ queryKey: SANDBOX_DRAFTS_KEY });
    },
  });
}

export interface DesignShareLink {
  id: string;
  token: string;
  expires_at: string;
  created_at: string;
  view_count: number;
  last_viewed_at: string | null;
}

/**
 * Mint a shareable preview link for a draft (migration 219).
 *
 * The API returns the token; the viewable URL is built here because only the
 * browser knows which host the storefront is served from — a tenant's admin and
 * storefront share an origin, so `window.location.origin` is the right base and
 * a server-side guess would be wrong for any tenant on a custom domain.
 */
export function useCreateShareLink() {
  return useMutation({
    mutationFn: async ({ draftId, days }: { draftId: string; days?: number }) => {
      const res = await api.post<{ link: DesignShareLink }>(
        `${BASE}/drafts/${draftId}/share`,
        days ? { expires_in_days: days } : {},
      );
      return {
        ...res.link,
        url: `${window.location.origin}/?design_share=${res.link.token}`,
      };
    },
  });
}
